import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { ChevronDownIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import void_ava from 'assets/gap_avatar.svg';
import clsx from 'clsx';
import { SSEEventType } from 'constants/sse';
import { Form, Formik } from 'formik';
import useSSEEvent from 'hooks/useSSEEvent';
import styled, { css, keyframes } from 'styled-components';
import { ProgressSpinner } from 'ui-kit/ProgressSpinner';
import Spinner from 'ui-kit/Spinner';
import { colors } from 'ui-kit/theme/colors';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  scenarioName: Yup.string()
    .required("Scenario name can't be empty")
    .trim()
});

export const ConfirmScenarioTool = props => {
  const {
    initialScenarioName = '',
    scenarioIdea = '',
    playerName = '',
    playerDescription = '',
    charactersNames = [],
    charactersDescription = {},
    charactersAvatars = {},
    isLoadingCreateScenario,
    setup = '',
    location = '',
    onBack = Function.prototype,
    onStart = Function.prototype,
    onChange = Function.prototype
  } = props;

  const [allowPublishing, setAllowPublishing] = useState(true);
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [avatarErrors, setAvatarErrors] = useState({});
  const [avatarRefreshKeys, setAvatarRefreshKeys] = useState({});

  const { data: avatarGenerationData } = useSSEEvent(SSEEventType.AVATAR_GENERATED);

  useEffect(() => {
    if (avatarGenerationData?.status === 'completed') {
      setAvatarRefreshKeys(prev => {
        const result = { ...prev };
        Object.keys(charactersAvatars).forEach(key => {
          result[key] = (result[key] || 0) + 1;
        });
        return result;
      });
    }
  }, [avatarGenerationData]);

  const handleImageError = (e, key) => {
    e.target.src = void_ava;
    setAvatarErrors(prev => ({ ...prev, [key]: true }));
  };

  return (
    <Wrapper>
      <Formik
        initialValues={{ scenarioName: initialScenarioName || '' }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          if (loading) return;
          setLoading(true);
          await onStart(values.scenarioName);
          setLoading(false);
        }}
      >
        {({ values, errors, touched, handleChange }) => (
          <Form>
            <Row className="flex flex-col items-start">
              <Label>Scenario name</Label>
              <Input
                placeholder="Scenario name"
                name="scenarioName"
                value={values.scenarioName}
                onChange={(evt) => {
                  handleChange(evt);
                  onChange(evt.target.value);
                }}
              />
              {errors.scenarioName && touched.scenarioName && (
                <ErrorMessage>{errors.scenarioName}</ErrorMessage>
              )}
            </Row>

            <Row className="cursor-pointer" onClick={() => setShowMore(!showMore)}>
              <Label>Review scenario configuration</Label>
              <ChevronDownIcon className={clsx(showMore && 'rotate-180', 'h-6 w-6 text-gray-400')} />
            </Row>

            {showMore && (
              <>
                <Row>
                  <Label>Scenario idea</Label>
                  <Value>{scenarioIdea}</Value>
                </Row>

                <Row>
                  <Label>Your character name</Label>
                  <Value>{playerName}</Value>
                </Row>

                <Row>
                  <Label>Your character description</Label>
                  <Value>{playerDescription}</Value>
                </Row>

                <Row>
                  <Label>Characters</Label>
                  <Value>{charactersNames.join(', ')}</Value>
                </Row>

                <Row>
                  <Label>Characters description</Label>
                </Row>

                {Object.keys(charactersDescription).map(key => {
                  const isGapAvatar = charactersAvatars[key] === 'gap';
                  const refreshKey = avatarRefreshKeys[key] || 0;
                  return (
                    <Row key={key}>
                      <div className="flex h-fit items-center gap-2">
                        <AvatarWrapper hasError={avatarErrors[key] && !isGapAvatar}>
                          <Avatar
                            key={`${key}-avatar-${refreshKey}`}
                            alt={'avatar'}
                            src={isGapAvatar ? void_ava : charactersAvatars[key]}
                            onError={(e) => handleImageError(e, key)}
                          />
                        </AvatarWrapper>
                        <Label>{key}</Label>
                      </div>
                      <CharacterDescription>{charactersDescription[key]}</CharacterDescription>
                    </Row>
                  );
                })}

                <Row>
                  <Label>Setup</Label>
                  <Value>{setup}</Value>
                </Row>

                <Row>
                  <Label>Location</Label>
                  <Value>{location}</Value>
                </Row>
              </>
            )}

            <div className="flex flex-col gap-2">
              <div
                className="flex cursor-pointer items-center gap-2"
                onClick={() => setAllowPublishing(!allowPublishing)}
              >
                <Checkbox
                  className="border-none focus:ring-0 focus:ring-transparent focus:ring-offset-0"
                  type="checkbox"
                  checked={allowPublishing}
                  onChange={() => { }}
                />
                <Description>Allow us to polish and publish this idea for all users</Description>
              </div>
              <Description>
                We will not access your chats. We will only review the idea and scenario settings you've
                provided above
              </Description>
            </div>

            <ButtonPlaceholder>
              {isMobile && (
                <MobileBackButton
                  type="button"
                  onClick={onBack}
                >
                  <ChevronLeftIcon />
                </MobileBackButton>
              )}
              {!isMobile && (
                <TransparentButton
                  type="button"
                  onClick={onBack}
                >
                  Back
                </TransparentButton>
              )}

              {isLoadingCreateScenario ? (
                <SpinnerWrapper>
                  <SpinnerText>Loading...</SpinnerText>
                  <ProgressSpinner size={40} />
                </SpinnerWrapper>
              ) : (
                <OrangeButton
                  isMobile={isMobile}
                  type="submit"
                >
                  {loading ? <Spinner /> : 'Start dialog'}
                </OrangeButton>
              )}
            </ButtonPlaceholder>
          </Form>
        )}
      </Formik>
    </Wrapper>
  );
};

const SpinnerWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const SpinnerText = styled.div`
  font-family: Nunito Sans;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  margin-right: 10px;
`;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 768px) {
    margin-bottom: 80px;
  }
`;

const Row = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const Label = styled.span`
  font-family: Nunito Sans;
  font-size: 16px;
  font-weight: 400;
  line-height: 18px;
`;

const Value = styled.span`
  max-width: 60%;
  font-family: Nunito Sans;
  font-size: 16px;
  font-weight: 400;
  line-height: 18px;
  text-align: right;
  color: rgba(169, 169, 169, 1);
`;

const Description = styled.span`
  color: rgba(169, 169, 169, 1);
  font-family: Nunito Sans;
  font-size: 12px;
  font-weight: 400;
  line-height: 14px;
`;

const CharacterDescription = styled(Value)`
  white-space: break-spaces;
`;

const AvatarWrapper = styled.div`
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  
  ${props => props.hasError && css`
    background: #131313;
    
    &::after {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(
        90deg,
        transparent 0%,
        transparent 40%,
        rgba(255, 255, 255, 0.04) 47%,
        rgba(255, 255, 255, 0.07) 50%,
        rgba(255, 255, 255, 0.04) 53%,
        transparent 60%,
        transparent 100%
      );
      transform: translate(-100%, -100%) rotate(45deg);
      animation: ${shimmerAnimation} 4s cubic-bezier(0.4, 0.0, 0.2, 1) infinite;
    }
  `}
`;

const Avatar = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

const Input = styled.input`
  background: rgba(19, 19, 19, 1);
  border-radius: 15px;
  color: white;
  font-size: 16px;
  line-height: 18px;
  outline: none;
  padding: 16px;
  border: 1px solid transparent;
  width: 100%;

  :focus {
    border-color: #FF7E55;
    box-shadow: none !important;
  }

  ::placeholder {
    color: rgba(105, 105, 105, 1);
  }
`;

const ButtonPlaceholder = styled.div`
  padding-top: 8px;
  width: 100%;
  display: flex;
  gap: 10px;
  margin-top: 16px;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    justify-content: unset;
    margin-top: auto;
    background: #1A1A1A;
    padding-top: 10px;
    padding-bottom: 16px;
    bottom: 0;
    position: fixed;
    width: 100%;
    z-index: 10;
  }
`;

const OrangeButton = styled.button`
  background-color: rgba(255, 126, 85, 1);
  width: ${props => props.isMobile ? '66%' : '100%'};
  padding: 8px 24px;
  border-radius: 100px;
  font-family: Nunito Sans;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  box-shadow: 0px 12px 40px 0px rgba(255, 126, 85, 0.12);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TransparentButton = styled(OrangeButton)`
  background-color: transparent;
  border: 1px solid rgba(255, 126, 85, 1);
  color: rgba(255, 126, 85, 1);
  box-shadow: none;
`;

const MobileBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 40px;
  background: #1A1A1A;
  border-radius: 100px;
  color: white;
  border: 1px solid #FF7E55;
  cursor: pointer;
  padding: 0;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  background: rgba(104, 104, 104, 1);
  border-radius: 20px;
  cursor: pointer;

  :checked {
    background-color: rgba(255, 126, 85, 1) !important;
  }
`;

const ErrorMessage = styled.span`
  color: ${colors.error};
  font-family: Nunito Sans;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
`;

const shimmerAnimation = keyframes`
  0% {
    transform: translate(-100%, -100%) rotate(45deg);
  }
  100% {
    transform: translate(100%, 100%) rotate(45deg);
  }
`;
